"use client";
import { Avatar } from "@base-ui/react/avatar";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { uploadFile } from "@/actions/file.action";
import { postTopicReply } from "@/actions/forum.action";
import type { PaginatedResult } from "@/lib/fetch";
import { getReplies } from "./actions";
import TopicReplyCard from "./topic-reply-card";
import TopicReplyEditor from "./topic-reply-editor";
import type { Reply } from "./types";

const REPLY_ESTIMATED_HEIGHT = 160;

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  topicId: string;
  initialData?: PaginatedResult<Reply>;
  isLoggedIn: boolean;
  currentUser?: CurrentUser;
}

const TopicCommentsClient = ({
  topicId,
  initialData,
  isLoggedIn,
  currentUser,
}: Props) => {
  const queryClient = useQueryClient();
  const queryKey = ["forum-replies", topicId];
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      getNextPageParam: (last) =>
        last && last.page < last.totalPages ? last.page + 1 : undefined,
      initialData: initialData
        ? { pageParams: [1], pages: [initialData] }
        : undefined,
      initialPageParam: 1,
      queryFn: async ({ pageParam }) => {
        const res = await getReplies(topicId, pageParam as number);
        if (!res.isSuccess) {
          throw new Error("Failed to fetch replies");
        }
        return res.result;
      },
      queryKey,
      staleTime: 30_000,
    });

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let imageUrls: string[] | undefined;
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((file) => uploadFile(file, "forum/replies")),
        );
        const urls = results.flatMap((r) => {
          return r.isSuccess ? [r.result.filePath] : [];
        });
        if (urls.length > 0) {
          imageUrls = urls;
        }
      }
      return postTopicReply(topicId, html, imageUrls);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const displayName = currentUser
    ? `${currentUser.name} ${currentUser.surname}`
    : "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("");

  const allReplies = data?.pages.flatMap((p) => p?.data ?? []) ?? [];

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allReplies.length + 1 : allReplies.length,
    estimateSize: () => REPLY_ESTIMATED_HEIGHT,
    overscan: 5,
    scrollMargin: containerRef.current?.offsetTop ?? 0,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }
    if (
      lastItem.index >= allReplies.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    allReplies.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return (
    <div>
      {isLoggedIn ? (
        <div className="flex gap-3 py-4 items-start">
          <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 shrink-0">
            {currentUser?.imageUrl && (
              <Avatar.Image
                className="size-full object-cover"
                src={currentUser.imageUrl}
              />
            )}
            <Avatar.Fallback className="flex size-full items-center justify-center text-sm font-medium text-gray-700">
              {initials}
            </Avatar.Fallback>
          </Avatar.Root>
          <div className="flex-1">
            <TopicReplyEditor
              isPending={isPending}
              onSubmit={(html, images) => mutate({ html, images })}
            />
          </div>
        </div>
      ) : null}

      <div
        ref={containerRef}
        style={{ height: virtualizer.getTotalSize(), position: "relative" }}
      >
        {virtualItems.map((item) => {
          const isLoader = item.index === allReplies.length;
          return (
            <div
              data-index={item.index}
              key={item.key}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)`,
                width: "100%",
              }}
            >
              {isLoader ? (
                <div className="py-4 text-center text-sm text-neutral-400">
                  Yükleniyor...
                </div>
              ) : (
                <TopicReplyCard
                  currentUser={currentUser}
                  isLoggedIn={isLoggedIn}
                  reply={allReplies[item.index]}
                  topicId={topicId}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopicCommentsClient;
